import { useEffect, useState } from "react";
import { AlertTriangle, Check, X } from "lucide-react";
import type { ChangeSetFileContent, ChangeSetInfo } from "@racler/protocol";
import { useRelay } from "../lib/relayContext.tsx";
import { dirtyConflicts, isFinal, statusText } from "../lib/review.ts";
import { ReviewDiff } from "./ReviewDiff.tsx";
import { ReviewFileList } from "./ReviewFileList.tsx";

interface Props {
  machineId: string;
  changeSet: ChangeSetInfo;
  /** Paths of open editor tabs with unsaved edits. Approval is blocked while they overlap with the proposal. */
  dirtyPaths: () => string[];
  onApprove: () => Promise<void>;
  onReject: () => Promise<void>;
  onClose: () => void;
}

/**
 * Diff review inside the IDE's editor area: compact file list (M/A/D/R) + Monaco diff editor + Approve / Reject.
 * The content is fetched from the machine and checked by the server against the frozen hash, so the diff under
 * review is the exact proposal that Approve applies.
 */
export function ReviewPane({ machineId, changeSet, dirtyPaths, onApprove, onReject, onClose }: Props) {
  const relay = useRelay();
  const [files, setFiles] = useState<ChangeSetFileContent[] | null>(null);
  const [selected, setSelected] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"approve" | "reject" | null>(null);
  const [dirty, setDirty] = useState<string[]>([]);
  const pending = changeSet.status === "pending_approval";

  useEffect(() => {
    let cancelled = false;
    setFiles(null);
    relay
      .chat(machineId, "agent/changeset.content", { changeSetId: changeSet.id })
      .then((r) => !cancelled && (setFiles(r.files), setSelected(0), setError(null)))
      .catch((e: unknown) => !cancelled && setError((e as Error).message));
    return () => {
      cancelled = true;
    };
  }, [relay, machineId, changeSet.id]);

  // The user may keep typing in another tab while reviewing: re-check unsaved edits continuously.
  useEffect(() => {
    const check = () => setDirty(dirtyConflicts(changeSet.files, dirtyPaths()));
    check();
    const t = setInterval(check, 500);
    return () => clearInterval(t);
  }, [changeSet.files, dirtyPaths]);

  const act = async (kind: "approve" | "reject") => {
    setBusy(kind);
    setError(null);
    try {
      await (kind === "approve" ? onApprove() : onReject());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="review" data-testid="review">
      <div className="review-head">
        <span className="review-title">Review changes</span>
        <span className="muted small" data-testid="review-status" data-status={changeSet.status}>
          {statusText(changeSet)}
        </span>
        <span className="review-actions">
          {pending && (
            <>
              <button disabled={busy !== null} onClick={() => void act("reject")} data-testid="review-reject">
                <X size={13} aria-hidden /> Reject
              </button>
              <button
                className="primary"
                disabled={busy !== null || dirty.length > 0 || files === null}
                onClick={() => void act("approve")}
                data-testid="review-approve"
              >
                <Check size={13} aria-hidden /> Approve
              </button>
            </>
          )}
          <button onClick={onClose} data-testid="review-close">
            Close
          </button>
        </span>
      </div>
      {pending && dirty.length > 0 && (
        <div className="review-warn" role="alert" data-testid="review-dirty">
          <AlertTriangle size={14} aria-hidden /> Unsaved edits in {dirty.join(", ")}. Save or close those
          tabs first; racler.dev never overwrites or auto-saves your edits.
        </div>
      )}
      {error && (
        <div className="msg-error" role="alert">
          {error}
        </div>
      )}
      {isFinal(changeSet.status) && changeSet.message && (
        <div className="muted small review-note">{changeSet.message}</div>
      )}
      <div className="review-body">
        <ReviewFileList
          files={files}
          summary={changeSet.files}
          selected={selected}
          loading={files === null && !error}
          onSelect={setSelected}
        />
        <ReviewDiff file={files?.[selected]} />
      </div>
    </div>
  );
}
